import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Stethoscope, Loader2, Check, X, FileText } from 'lucide-react';
import { ProfessionalStatusBadge } from '@/components/professional/ProfessionalCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/professional`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

export default function ProfessionalsAdminPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API, { headers: auth(), params: status === 'all' ? {} : { status } });
      setItems(data);
    } catch { toast.error('Erro ao carregar perfis'); } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await axios.post(`${API}/${id}/approve`, {}, { headers: auth() }); toast.success('Perfil aprovado'); load(); }
    catch { toast.error('Erro ao aprovar'); }
  };

  const reject = async () => {
    try {
      await axios.post(`${API}/${rejecting}/reject`, { reason }, { headers: auth() });
      toast.success('Perfil rejeitado'); setRejecting(null); setReason(''); load();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || 'Informe o motivo'); }
  };

  const openCertificate = async (id) => {
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${API}/${id}/certificate`, { headers: auth(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url; else window.location.assign(url);
    } catch { win?.close(); toast.error('Certificado indisponível'); }
  };

  return (
    <div data-testid="admin-professionals-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Stethoscope className="h-6 w-6 text-purple-500" />Perfis Profissionais</h1>
          <p className="text-sm text-zinc-400 mt-1">Validar credenciais enviadas pelos profissionais</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-zinc-800 border-zinc-700 text-white" data-testid="admin-pro-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Aguardando</SelectItem>
            <SelectItem value="approved">Aprovados</SelectItem>
            <SelectItem value="rejected">Rejeitados</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>
        : items.length === 0 ? <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-8 text-center text-zinc-500">Nenhum perfil nesta lista.</CardContent></Card>
        : <div className="space-y-3">{items.map((p) => (
          <Card key={p.user_id} className="bg-zinc-900 border-zinc-800" data-testid={`admin-pro-item-${p.user_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-white flex-1">{p.professional_name} <span className="text-xs text-zinc-500">({p.user_email})</span></p>
                <ProfessionalStatusBadge status={p.status} />
              </div>
              <p className="text-sm text-zinc-400">{[p.specialty, p.institution, p.registry].filter(Boolean).join(' · ')}</p>
              {p.short_cv && <p className="text-sm text-zinc-500 whitespace-pre-line">{p.short_cv}</p>}
              {p.reject_reason && <p className="text-xs text-red-400">Motivo: {p.reject_reason}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                {p.has_certificate && <Button size="sm" variant="outline" className="gap-1 bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 hover:text-white" onClick={() => openCertificate(p.user_id)} data-testid={`admin-pro-cert-${p.user_id}`}><FileText className="h-4 w-4" />Ver certificado</Button>}
                {p.status !== 'approved' && <Button size="sm" className="gap-1 bg-green-600 hover:bg-green-700" onClick={() => approve(p.user_id)} data-testid={`admin-pro-approve-${p.user_id}`}><Check className="h-4 w-4" />Aprovar</Button>}
                {p.status !== 'rejected' && <Button size="sm" variant="destructive" className="gap-1" onClick={() => setRejecting(p.user_id)} data-testid={`admin-pro-reject-${p.user_id}`}><X className="h-4 w-4" />Rejeitar</Button>}
              </div>
            </CardContent>
          </Card>
        ))}</div>}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="admin-pro-reject-dialog">
          <DialogHeader><DialogTitle>Motivo da rejeição</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explique o que precisa ser corrigido" data-testid="admin-pro-reject-reason" />
          <DialogFooter><Button variant="destructive" disabled={reason.trim().length < 3} onClick={reject} data-testid="admin-pro-reject-confirm">Rejeitar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
